import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { devtools } from 'zustand/middleware'
import type { User, StudentProfile } from '@shared/types/user'
import { studentApi } from '../services/api'
import toast from 'react-hot-toast'

interface AuthState {
  user: User | null
  studentProfile: StudentProfile | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>
  loginWithPhone: (phone: string, password: string) => Promise<void>
  register: (userData: any) => Promise<void>
  logout: () => void
  updateProfile: (profileData: Partial<StudentProfile>) => Promise<void>
  fetchProfile: () => Promise<void>
  setToken: (token: string) => void
  clearAuth: () => void
}

export const useAuthStore = create<AuthState & AuthActions>()(
  devtools(
    persist(
      (set, get) => ({
        // 初始状态
        user: null,
        studentProfile: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,

        // 登录 (邮箱)
        login: async (email: string, password: string) => {
          set({ isLoading: true })
          try {
            // 演示模式：检查是否为测试账号
            if ((email === 'student@talknect.com' && password === '123456') ||
                (email === 'demo@talknect.com' && password === 'demo')) {

              const mockUser: any = {
                id: 1,
                email: email,
                full_name: '张小明',
                phone: '13800138000',
                user_type: 'student' as const,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              }

              const mockStudentProfile = {
                id: 1,
                user_id: 1,
                first_name: '小明',
                last_name: '张',
                date_of_birth: '1995-05-15',
                gender: 'male' as const,
                country: 'China',
                city: 'Beijing',
                timezone: 'Asia/Shanghai',
                language_level: 'intermediate' as const,
                learning_goals: 'Business English, IELTS preparation',
                preferred_schedule: 'Weekdays after 6pm, weekends',
                balance_hours: 5,
                total_lessons: 12,
                total_hours_consumed: 7,
                average_rating: 4.8,
                is_verified: true,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              }

              set({
                user: mockUser,
                studentProfile: mockStudentProfile,
                token: 'demo-token-student',
                isAuthenticated: true,
                isLoading: false
              })

              toast.success('登录成功！欢迎回来，' + mockUser.full_name)
              return
            }

            // 真实API调用
            const response = await studentApi.post('/auth/login', {
              email,
              password,
              user_type: 'student'
            })

            if (response.success && response.data) {
              const { user, token, refreshToken } = response.data as any

              set({
                user,
                token,
                refreshToken: refreshToken || null,
                isAuthenticated: true,
                isLoading: false
              })

              toast.success('登录成功！')

              // 获取学生资料
              await get().fetchProfile()
            } else {
              throw new Error(response.error?.message || '登录失败')
            }
          } catch (error: any) {
            set({ isLoading: false })
            toast.error('邮箱或密码错误，请重试')
            throw error
          }
        },

        // 登录 (手机号)
        loginWithPhone: async (phone: string, password: string) => {
          set({ isLoading: true })
          try {
            const response = await studentApi.post('/auth/login', {
              phone,
              password,
              user_type: 'student'
            })

            if (response.success && response.data) {
              const { user, token, refreshToken } = response.data as any
              
              set({
                user,
                token,
                refreshToken: refreshToken || null,
                isAuthenticated: true,
                isLoading: false
              })

              toast.success('登录成功！')
              
              // 获取学生资料
              await get().fetchProfile()
            } else {
              throw new Error(response.error?.message || '登录失败')
            }
          } catch (error: any) {
            set({ isLoading: false })
            toast.error(error.message || '登录失败')
            throw error
          }
        },

        // 注册
        register: async (userData: any) => {
          set({ isLoading: true })
          try {
            const response = await studentApi.post('/auth/register', {
              ...userData,
              user_type: 'student'
            })

            if (response.success) {
              toast.success('注册成功！')
              set({ isLoading: false })
            } else {
              throw new Error(response.error?.message || '注册失败')
            }
          } catch (error: any) {
            set({ isLoading: false })
            toast.error(error.message || '注册失败')
            throw error
          }
        },

        // 登出
        logout: () => {
          localStorage.removeItem('talknect-student-auth')
          set({
            user: null,
            studentProfile: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false
          })
          toast.success('已退出登录')
        },

        // 更新资料
        updateProfile: async (profileData: Partial<StudentProfile>) => {
          try {
            const response = await studentApi.put('/users/profile', profileData)
            
            if (response.success && response.data) {
              // 同时更新 user 和 studentProfile
              const userData = response.data as any
              set(state => ({
                user: {
                  ...(state.user as any),
                  full_name: userData.full_name || (state.user as any)?.full_name,
                  avatar_url: userData.avatar_url || (state.user as any)?.avatar_url
                },
                studentProfile: { ...state.studentProfile, ...response.data }
              }))
              toast.success('资料更新成功！')
            } else {
              throw new Error(response.error?.message || '更新失败')
            }
          } catch (error: any) {
            toast.error(error.message || '更新失败')
            throw error
          }
        },

        // 获取资料
        fetchProfile: async () => {
          try {
            const response = await studentApi.get('/users/profile')
            
            if (response.success && response.data) {
              // 同时更新 user 和 studentProfile
              const userData = response.data as any
              set(state => ({
                user: {
                  ...(state.user as any),
                  full_name: userData.full_name || (state.user as any)?.full_name,
                  avatar_url: userData.avatar_url || (state.user as any)?.avatar_url
                },
                studentProfile: response.data
              }))
            }
          } catch (error) {
            // 静默处理错误
          }
        },

        // 设置 Token
        setToken: (token: string) => {
          set({ token })
        },

        // 清除认证状态
        clearAuth: () => {
          localStorage.removeItem('talknect-student-auth')
          set({
            user: null,
            studentProfile: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false
          })
        }
      }),
      {
        name: 'talknect-student-auth',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          token: state.token,
          refreshToken: state.refreshToken,
          user: state.user,
          studentProfile: state.studentProfile,
          isAuthenticated: state.isAuthenticated
        })
      }
    ),
    { name: 'auth-store' }
  )
)